import { defineStore } from 'pinia';
import { computed, reactive, ref, toRefs, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { useWebSocket } from '@vueuse/core';
import {
  Avatar,
  CreateGameRequest,
  CreateGameResponse,
  GameOptions,
  GameWebsocketMessage,
  NextQuestionRequest,
  ScrubbedQuestion,
  ShowResultsMessage,
  ShowResultsRequest,
  StartGameRequest,
  GameSnapshot,
  SetGameOptionsRequest,
  PeekQuestionPhraseRequest,
  Player,
  GetMyQuiztersResponse,
  GetQuizterResponse,
  Quizter,
  SaveQuizterRequest,
  SaveQuizterResponse,
  SharedQuizter,
  GetMySubscribedQuiztersResponse,
  ShareQuizterResponse,
  CheckQuizterShareResponse,
  GetAnswerRecapMatrixRequest,
  AnswerRecapMatrix,
} from '@ilteducation/quizter-types';
import { Actions, Messages, ErrorType, PlayerRemovedReason } from '@ilteducation/quizter-types/game-api';
import { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import { ErrorGameStates, GameState, SOCKET_URL } from '../util';
import { decodeAnswerRecapMatrix, type AnswersByQuestion, type PlayedQuestion } from '../util/answerRecapMatrix';
import { configHelper } from '@ilteducation/config';
import axios from 'axios';
import { handleError } from '../util/errorHandlers';
import { useQuizterTracking } from '../composables/useQuizterTracking';

export type JoinedPlayer = Omit<Player, 'scores' | 'answers' | 'connectionVersion'> & { offline: boolean };

export const useQuizterStore = defineStore(
  'quizter',
  () => {
    // === GAME STATE  ===
    type ReturnParams = {
      url: string;
      text: string;
    };

    const offlineTimers = new Map<string, ReturnType<typeof setTimeout>>();
    const OFFLINE_GRACE_PERIOD_MS = 10000;

    const createInitialGameState = () => ({
      gameId: null as string | null,
      activityId: null as string | null,
      resourceId: null as string | null,
      resourceIds: [] as string[],
      currentQuestion: undefined as ScrubbedQuestion | undefined,
      currentQuestionIndex: -1,
      numberOfQuestions: undefined as number | undefined,
      state: GameState.INIT,
      results: [] as ShowResultsMessage['payload']['results'],
      currentQuestionAnswer: null as string | null,
      questionAnswers: {} as Record<string, Record<string, number>>,
      selectedAnswerId: null as string | null,
      joinedPlayers: [] as JoinedPlayer[],
      myAvatar: undefined as Avatar | undefined,
      playerId: null as string | null,
      // Only sent in this player's own copy of playerJoined, and only for a game storing results.
      myDisplayName: null as string | null,
      // The host's market, sent with a login requirement - the player signs in to the same one.
      loginRealm: undefined as string | undefined,
      questionCountdownStartedAt: undefined as number | undefined,
      gameOptions: {
        questionAnswerTimeout: 20000,
        useScoring: false,
        useTimer: false,
        useQuestionShowTimer: true,
        questionShowTimeout: 5000,
        showAnswerRecap: false,
      } as GameOptions,
      answersReceivedForCurrentQuestion: 0,
      maxPlayers: 0,
      // Host only.
      playedQuestions: [] as PlayedQuestion[],
      answersByQuestion: {} as AnswersByQuestion,
      playerNames: {} as Record<string, string>,
    });

    const gameState = reactive(createInitialGameState());

    // === TRACKING ===
    const gameStateRefs = toRefs(gameState);
    const { isHost, sendGameStartEvent, sendGameEndEvent, resetTracking } = useQuizterTracking({
      gameId: gameStateRefs.gameId,
      resourceId: gameStateRefs.resourceId,
      resourceIds: gameStateRefs.resourceIds,
      gameOptions: gameStateRefs.gameOptions,
      joinedPlayersCount: computed(() => gameState.joinedPlayers.length),
      numberOfQuestions: gameStateRefs.numberOfQuestions,
    });

    // === UI STATE (not part of game state, not reset) ===
    const isConnected = computed(() => status.value === 'OPEN');
    const now = ref<number>(Date.now());
    const isLoading = ref(false);
    const isRemoving = ref<Record<Player['id'], boolean>>({});
    const isReconnecting = ref(false);
    const isLoadingAnswerRecap = ref(false);
    const returnParams = ref<ReturnParams>();
    const isGameFinished = computed(
      () => gameState.numberOfQuestions && gameState.currentQuestionIndex >= gameState.numberOfQuestions - 1,
    );
    // Error handling
    const errorType = ref<ErrorType | null>(null);
    const errorMessage = ref<string | null>(null);

    // === COMPUTED ===
    const allPlayersHaveAnswered = computed(
      () =>
        gameState.joinedPlayers.length > 0 &&
        gameState.joinedPlayers.length === gameState.answersReceivedForCurrentQuestion,
    );

    // Question countdown state (for each question) - computed based on timestamp
    const questionCountdown = computed(() => {
      if (
        !gameState.questionCountdownStartedAt ||
        !gameState.gameOptions.questionShowTimeout ||
        !gameState.gameOptions.useQuestionShowTimer
      ) {
        return 0;
      }
      const showTimeoutMs = gameState.gameOptions.questionShowTimeout;
      const timeElapsed = now.value - gameState.questionCountdownStartedAt;
      return Math.max(0, showTimeoutMs - timeElapsed);
    });

    // Compute when answer phase should start (replaces setTimeout callback)
    const answerPhaseStartedAt = computed(() => {
      if (!gameState.questionCountdownStartedAt) {
        return undefined;
      }
      if (gameState.gameOptions.useQuestionShowTimer && !!gameState.gameOptions.questionShowTimeout) {
        return gameState.questionCountdownStartedAt + gameState.gameOptions.questionShowTimeout;
      }
      return gameState.questionCountdownStartedAt;
    });

    const isShowingResults = computed(
      () => gameState.state === GameState.SHOW_RESULTS || gameState.state === GameState.SHOW_CORRECT_ANSWER,
    );

    const timeLeft = computed(() => {
      if (!answerPhaseStartedAt.value || !gameState.gameOptions.questionAnswerTimeout) {
        return +Infinity;
      }
      const answerTimeout = gameState.gameOptions.questionAnswerTimeout;
      const timeElapsed = now.value - answerPhaseStartedAt.value;
      return Math.max(0, answerTimeout - timeElapsed);
    });

    const isLobbyFull = computed(() => {
      return gameState.maxPlayers > 0 && gameState.joinedPlayers.length >= gameState.maxPlayers;
    });

    // === WEBSOCKET ===
    const { send, open, close, status } = useWebSocket(SOCKET_URL, {
      immediate: false,
      autoReconnect: true,
      onMessage: (_ws, event) => {
        handleMessage(event);
      },
    });

    // === MESSAGE HANDLER ===
    function handleMessage(event: MessageEvent) {
      try {
        const parsedMessage = JSON.parse(event.data) as GameWebsocketMessage;

        switch (parsedMessage.message) {
          case Messages.peekQuestionPhrase:
            gameState.answersReceivedForCurrentQuestion = 0;
            gameState.selectedAnswerId = null;
            gameState.currentQuestion = parsedMessage.payload.question;
            gameState.state = GameState.SHOW_QUESTION_PHRASE;
            isLoading.value = false;
            isReconnecting.value = false;
            break;

          case Messages.showQuestion:
            if (parsedMessage.payload.questionIndex === 0) {
              sendGameStartEvent();
            }

            gameState.answersReceivedForCurrentQuestion = 0;
            if (gameState.currentQuestionIndex !== parsedMessage.payload.questionIndex) {
              gameState.selectedAnswerId = null;
            }
            gameState.currentQuestionIndex = parsedMessage.payload.questionIndex;
            gameState.currentQuestion = parsedMessage.payload.question;
            gameState.state = GameState.SHOW_QUESTIONS;
            isLoading.value = false;
            isReconnecting.value = false;
            // Use backend timestamp for countdown synchronization
            gameState.questionCountdownStartedAt = parsedMessage.payload.questionTimerStartedAt;
            setServerTimeAnchor(parsedMessage.payload.serverNowMs);
            break;

          case Messages.showCorrectAnswer: {
            gameState.currentQuestion = parsedMessage.payload.question;
            gameState.currentQuestionAnswer = parsedMessage.payload.correctAnswer;
            gameState.questionAnswers[gameState.currentQuestionIndex] = parsedMessage.payload.answerDistribution;
            gameState.state = GameState.SHOW_CORRECT_ANSWER;
            isLoading.value = false;
            isReconnecting.value = false;
            break;
          }

          case Messages.showResults:
            gameState.results = parsedMessage.payload.results;
            gameState.currentQuestionAnswer = parsedMessage.payload.currentQuestionAnswer;
            isLoading.value = false;
            isReconnecting.value = false;
            gameState.state = GameState.SHOW_RESULTS;
            break;

          case Messages.answerRecapMatrix:
            applyAnswerRecapMatrix(parsedMessage.payload);
            isLoadingAnswerRecap.value = false;
            break;

          case Messages.gameIsReady:
            gameState.gameOptions = parsedMessage.payload.gameOptions;
            gameState.numberOfQuestions = parsedMessage.payload.quizLength;
            isLoading.value = false;
            gameState.state = GameState.READY;
            break;

          case Messages.hostJoined:
            isHost.value = true;
            gameState.maxPlayers = parsedMessage.payload.maxPlayers || 0;
            if (parsedMessage.payload.snapshot) {
              hydrate(parsedMessage.payload.snapshot);
              if (parsedMessage.payload.snapshot.returnParams) {
                returnParams.value = parsedMessage.payload.snapshot.returnParams;
              }
              // Only clear loading and reconnecting for lobby/settings reconnection, since it will be cleared later in lastBroadcast when reconnecting
              if (gameState.state === GameState.SETTINGS || gameState.state === GameState.LOBBY) {
                isLoading.value = false;
                isReconnecting.value = false;

                if (gameState.state === GameState.LOBBY) {
                  // For lobby state, also start offline timers for any currently disconnected players in the snapshot, since they may not have been cleaned up yet
                  gameState.joinedPlayers.forEach((p) => {
                    if (p.status.value === 'disconnected') {
                      startTimerUntilOffline(p.id);
                    }
                  });
                }
              }
            } else {
              // Fresh join (no snapshot): clear loading/reconnecting immediately since there's no prior state to restore
              isLoading.value = false;
              isReconnecting.value = false;
            }
            break;

          case Messages.playerJoined:
            // Handle returnParams from the message (for new players)
            if (parsedMessage.payload.returnParams && !isHost.value) {
              returnParams.value = parsedMessage.payload.returnParams;
            }

            if (parsedMessage.payload.displayName) {
              gameState.myDisplayName = parsedMessage.payload.displayName;
            }

            if (parsedMessage.payload.snapshot && gameState.playerId) {
              hydrate(parsedMessage.payload.snapshot);
              // Don't clear isReconnecting yet when reconnecting mid-game (e.g. SHOW_QUESTIONS,
              // SHOW_CORRECT_ANSWER). The snapshot may be slightly stale, and the subsequent
              // broadcast message (showQuestion, showCorrectAnswer, showResults) will clear it
              // once the player has the definitive latest state. For pre-game states there is no
              // pending broadcast to wait for, so it is safe to clear immediately.
              if (
                gameState.state === GameState.SETTINGS ||
                gameState.state === GameState.LOBBY ||
                gameState.state === GameState.INIT
              ) {
                isReconnecting.value = false;
              }
            } else {
              // Don't add player to joined list if already there, just update status
              const existingPlayer = gameState.joinedPlayers.find((p) => p.id === parsedMessage.payload.id);
              if (existingPlayer) {
                gameState.joinedPlayers = gameState.joinedPlayers.map((p) => {
                  if (p.id === parsedMessage.payload.id) {
                    return { ...p, status: parsedMessage.payload.status, offline: false };
                  }
                  return p;
                });
              } else {
                gameState.joinedPlayers.push({
                  id: parsedMessage.payload.id,
                  avatar: parsedMessage.payload.avatar,
                  status: parsedMessage.payload.status,
                  offline: false,
                });
              }
              isReconnecting.value = false;
            }
            break;

          case Messages.playerLeft:
            gameState.joinedPlayers = gameState.joinedPlayers.map((p) => {
              if (p.id === parsedMessage.payload.playerId) {
                return { ...p, status: parsedMessage.payload.playerStatus };
              }
              return p;
            });
            if (gameState.state === GameState.LOBBY) {
              startTimerUntilOffline(parsedMessage.payload.playerId);
            }
            break;

          case Messages.playerRemoved:
            isRemoving.value[parsedMessage.payload.playerId] = false;
            gameState.joinedPlayers = gameState.joinedPlayers.filter((p) => p.id !== parsedMessage.payload.playerId);
            if (parsedMessage.payload.playerId !== gameState.playerId) {
              break;
            }
            if (parsedMessage.payload.reason === PlayerRemovedReason.loginRequired) {
              // Try to rejoin as a new player
              gameState.playerId = null;
              gameState.myAvatar = undefined;
              gameState.myDisplayName = null;
              gameState.selectedAnswerId = null;
              gameState.loginRealm = parsedMessage.payload.realm;
              gameState.state = GameState.LOGIN_REQUIRED;
              errorType.value = ErrorType.LOGIN_REQUIRED;
              errorMessage.value = null;
              break;
            }
            reset();
            // redirect to quizter.com when we have the domain
            window.location.href = returnParams.value?.url || '/';
            break;

          case Messages.avatarAssigned:
            gameState.myAvatar = parsedMessage.payload.avatar;
            gameState.playerId = parsedMessage.payload.playerId;
            /*
             The join succeeded, so drop whatever error the previous attempt left behind.
             */
            if (ErrorGameStates.has(gameState.state)) {
              gameState.state = GameState.INIT;
              errorType.value = null;
              errorMessage.value = null;
            }
            break;

          case Messages.answerSubmitted:
            gameState.answersReceivedForCurrentQuestion += 1;
            break;

          case Messages.error:
            isLoading.value = false;
            isReconnecting.value = false;
            isLoadingAnswerRecap.value = false;
            ({ errorType: errorType.value, errorMessage: errorMessage.value } = handleError(parsedMessage.payload));
            if (errorType.value === ErrorType.GAME_NOT_FOUND) {
              gameState.state = GameState.GAME_NOT_FOUND;
            } else if (errorType.value === ErrorType.PLAYER_CANNOT_JOIN) {
              gameState.state = GameState.PLAYER_CANNOT_JOIN;
            } else if (
              errorType.value === ErrorType.LOGIN_REQUIRED ||
              errorType.value === ErrorType.INVALID_ACCESS_TOKEN
            ) {
              gameState.loginRealm = parsedMessage.payload.realm;
              gameState.state = GameState.LOGIN_REQUIRED;
            } else if (errorType.value === ErrorType.SESSION_SUPERSEDED) {
              gameState.state = GameState.SESSION_SUPERSEDED;
            } else {
              gameState.state = GameState.UNHANDLED_ERROR;
            }
            break;

          default:
            console.warn(`Unexpected message type: ${(parsedMessage as any).message}`);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    }

    // === ANSWER RECAP MATRIX (host only) ===
    function applyAnswerRecapMatrix(payload: AnswerRecapMatrix) {
      const { questions, answersByQuestion } = decodeAnswerRecapMatrix(
        payload.quiz,
        payload.answerPlayerOrder,
        payload.answersByQuestion,
      );
      gameState.playedQuestions = questions;
      gameState.answersByQuestion = answersByQuestion;
      gameState.playerNames = payload.playerNames;
    }

    // === OFFLINE TIMEOUT MANAGEMENT ===
    const startTimerUntilOffline = (playerId: string) => {
      if (offlineTimers.has(playerId)) {
        clearTimeout(offlineTimers.get(playerId));
      }
      const player = gameState.joinedPlayers.find((p) => p.id === playerId);
      if (!player) {
        return;
      }
      const lastSeen = player.status.lastSeen;
      const timeSinceLastSeen = Date.now() - lastSeen;
      const timeUntilOffline = Math.max(OFFLINE_GRACE_PERIOD_MS - timeSinceLastSeen, 0);
      if (timeUntilOffline === 0) {
        // Grace period already passed, set to offline immediately
        gameState.joinedPlayers = gameState.joinedPlayers.map((p) => {
          if (p.id === playerId) {
            return { ...p, offline: true };
          }
          return p;
        });
        return;
      }
      offlineTimers.set(
        playerId,
        setTimeout(() => {
          // Set status to offline after grace period, to differentiate between recently disconnected (may reconnect soon) vs long gone players
          const currentStatus = gameState.joinedPlayers.find((p) => p.id === playerId)?.status.value;
          if (currentStatus === 'disconnected') {
            // Still disconnected, set to offline
            gameState.joinedPlayers = gameState.joinedPlayers.map((p) => {
              if (p.id === playerId) {
                return { ...p, offline: true };
              }
              return p;
            });
          }
          offlineTimers.delete(playerId);
        }, timeUntilOffline),
      );
    };

    // === TIMER MANAGEMENT ===
    const serverAtT0 = ref<number | null>(null);
    const t0Mono = ref<number | null>(null);

    const setServerTimeAnchor = (serverNowMs: number) => {
      serverAtT0.value = serverNowMs;
      t0Mono.value = performance.now();
    };

    const estimatedServerNowMs = (): number => {
      if (serverAtT0.value == null || t0Mono.value == null) return Date.now(); // fallback
      return serverAtT0.value + (performance.now() - t0Mono.value);
    };

    let rafId: number | null = null;
    function startTimer() {
      const tick = () => {
        now.value = estimatedServerNowMs();
        rafId = requestAnimationFrame(tick);
      };
      if (rafId == null) tick();
    }
    function stopTimer() {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = null;
    }

    // === ACTIONS ===
    function connect() {
      const route = useRoute();
      const gameId = route.params.gameId?.toString();
      if (gameId === gameState.gameId && gameState.state === GameState.GAME_NOT_FOUND) {
        return;
      }
      // Loading indication for reconnection
      if (gameState.gameId) {
        isLoading.value = true;
        isReconnecting.value = true;
      }

      startTimer();
      open();
    }

    function disconnect() {
      stopTimer();
      close();
    }

    function hydrate(snapshot: GameSnapshot) {
      const { players, gameOptions, ...rest } = snapshot;
      const joinedPlayers: JoinedPlayer[] = Object.values(players).map((p: any) => ({
        id: p.id,
        avatar: p.avatar,
        status: p.status,
        offline: false,
      }));

      Object.assign(gameState, rest);
      gameState.joinedPlayers = joinedPlayers;
      gameState.gameOptions = gameOptions;

      // Extract answer for current question
      if (gameState.playerId) {
        const myPlayer = Object.values(players).find((p: any) => p.id === gameState.playerId);
        if (myPlayer && myPlayer.answers && rest.currentQuestionId) {
          const answers = (myPlayer as any).answers;
          // Look up answer for current question index
          const answerForCurrentQuestion = answers[rest.currentQuestionId];
          if (answerForCurrentQuestion) {
            gameState.selectedAnswerId = answerForCurrentQuestion.option;
          } else {
            gameState.selectedAnswerId = null;
          }
        }
      }
    }

    function setSelectedAnswer(answerId: string) {
      gameState.selectedAnswerId = answerId;
    }

    function sendMessage<T>(message: T): void {
      if (!isConnected.value) {
        console.error('Cannot send message: WebSocket not connected');
        return;
      }
      send(JSON.stringify(message));
    }

    function setGameId(id: string) {
      gameState.gameId = id;
    }

    function setActivityId(id: string) {
      gameState.activityId = id;
    }

    function setResourceId(id: string) {
      gameState.resourceId = id;
    }

    function reset() {
      Object.assign(gameState, createInitialGameState());
      sessionStorage.removeItem('quizter');
      isLoading.value = false;
      resetTracking();
      isReconnecting.value = false;
    }

    function setLoading(loading: boolean) {
      isLoading.value = loading;
    }

    const quizterUrl = configHelper.getApiUrl('quizter', true);
    const joinGameHost = async (gId: string) => {
      const joinGameHostRequest = {
        action: Actions.joinGameHost,
        gameId: gId,
      };
      sendMessage(joinGameHostRequest);
    };
    const createGameUrl = configHelper.isLocal() ? 'http://localhost:3007/create-game' : `${quizterUrl}/create-game`;
    const myQuiztersUrl = configHelper.isLocal() ? 'http://localhost:3007/my-quizters' : `${quizterUrl}/my-quizters`;
    const saveQuizterUrl = configHelper.isLocal() ? 'http://localhost:3007/save-quizter' : `${quizterUrl}/save-quizter`;
    const getQuizterUrl = (activityId: string) =>
      configHelper.isLocal() ? `http://localhost:3007/quizters/${activityId}` : `${quizterUrl}/quizters/${activityId}`;
    const updateQuizterUrl = (activityId: string) =>
      configHelper.isLocal()
        ? `http://localhost:3007/update-quizter/${activityId}`
        : `${quizterUrl}/update-quizter/${activityId}`;
    const quizterApiBase = configHelper.isLocal() ? 'http://localhost:3007' : quizterUrl;
    const shareQuizterUrl = (activityId: string) => `${quizterApiBase}/quizters/${activityId}/share`;
    const mySubscribedQuiztersUrl = `${quizterApiBase}/my-subscribed-quizters`;
    const sharedQuizterUrl = (shareId: string) => `${quizterApiBase}/shared-quizters/${shareId}`;

    const createGame = async (
      resourceIds: string[],
      useDemoQuiz: boolean,
      activityId?: string,
      questions?: CloseEndedQuestion[],
      rParams?: ReturnParams,
      rParamsPlayer?: ReturnParams,
    ) => {
      //First reset to clear any previous game state
      reset();
      if (rParams) {
        returnParams.value = rParams;
      }
      isHost.value = true;
      setLoading(true);
      const createGameRequest: CreateGameRequest = {
        resourceIds: resourceIds,
        isDemo: useDemoQuiz,
        activityId,
        questions,
        ...(returnParams.value && { returnParams: returnParams.value }),
        ...(rParamsPlayer && { returnParamsPlayer: rParamsPlayer }),
      };
      try {
        setResourceId(resourceIds[0]);
        if (activityId) {
          setActivityId(activityId);
        }
        gameState.resourceIds = resourceIds;
        const response = await axios.post<CreateGameResponse>(createGameUrl, createGameRequest);
        setGameId(response.data.gameId);
        gameState.state = GameState.SETTINGS;
      } finally {
        setLoading(false);
      }
    };

    const fetchMyQuizters = async (): Promise<Quizter[]> => {
      const { data } = await axios.get<GetMyQuiztersResponse>(myQuiztersUrl);
      return data.quizters;
    };

    const saveQuizter = async (request: SaveQuizterRequest): Promise<string> => {
      const response = await axios.post<SaveQuizterResponse>(saveQuizterUrl, request);
      return response.data.activityId;
    };

    const getQuizter = async (activityId: string): Promise<GetQuizterResponse> => {
      const { data } = await axios.get<GetQuizterResponse>(getQuizterUrl(activityId));
      return data;
    };

    const updateQuizter = async (activityId: string, request: SaveQuizterRequest): Promise<string> => {
      const response = await axios.put<SaveQuizterResponse>(updateQuizterUrl(activityId), request);
      return response.data.activityId;
    };

    const deleteQuizter = async (activityId: string): Promise<void> => {
      await axios.delete(`${myQuiztersUrl}/${encodeURIComponent(activityId)}`);
    };

    const shareQuizter = async (activityId: string): Promise<string> => {
      const { data } = await axios.post<ShareQuizterResponse>(shareQuizterUrl(activityId));
      return data.shareId;
    };

    const checkQuizterShare = async (activityId: string): Promise<string | null> => {
      const { data } = await axios.get<CheckQuizterShareResponse>(shareQuizterUrl(activityId));
      return data.shareId;
    };

    const unshareQuizter = async (shareId: string): Promise<void> => {
      try {
        await axios.delete(sharedQuizterUrl(shareId));
      } catch (error) {
        // A 404 means the share is already gone — safe to treat as already un-shared.
        if (!axios.isAxiosError(error) || error.response?.status !== 404) {
          throw error;
        }
      }
    };

    const fetchMySubscribedQuizters = async (): Promise<SharedQuizter[]> => {
      const { data } = await axios.get<GetMySubscribedQuiztersResponse>(mySubscribedQuiztersUrl);
      return data.quizters;
    };

    const getSharedQuizter = async (shareId: string): Promise<GetQuizterResponse> => {
      const { data } = await axios.get<GetQuizterResponse>(sharedQuizterUrl(shareId));
      return data;
    };

    const subscribeSharedQuizter = async (shareId: string): Promise<void> => {
      await axios.post(`${sharedQuizterUrl(shareId)}/subscribe`);
    };

    const unsubscribeSharedQuizter = async (shareId: string): Promise<void> => {
      await axios.post(`${sharedQuizterUrl(shareId)}/unsubscribe`);
    };

    const setGameOptions = (options: GameOptions) => {
      if (!gameState.gameId) {
        return;
      }
      const req: SetGameOptionsRequest = {
        action: Actions.setGameOptions,
        gameId: gameState.gameId,
        ...options,
      };
      sendMessage(req);
    };

    const startGame = () => {
      if (!gameState.gameId) {
        return;
      }

      setLoading(true);
      const req: StartGameRequest = {
        action: Actions.startGame,
        gameId: gameState.gameId,
      };
      sendMessage(req);
    };

    const showQuestionPhrase = () => {
      if (!gameState.gameId) return;
      setLoading(true);

      const req: PeekQuestionPhraseRequest = {
        action: Actions.peekQuestionPhrase,
        gameId: gameState.gameId,
      };
      sendMessage(req);
    };

    const nextQuestion = () => {
      if (!gameState.gameId) return;

      setLoading(true);
      const req: NextQuestionRequest = {
        action: Actions.nextQuestion,
        gameId: gameState.gameId,
      };
      sendMessage(req);
    };

    const showCorrectAnswer = () => {
      if (
        !gameState.gameId ||
        gameState.state === GameState.SHOW_CORRECT_ANSWER ||
        gameState.state === GameState.SHOW_RESULTS
      ) {
        return;
      }

      setLoading(true);
      const req = {
        action: Actions.showCorrectAnswer,
        gameId: gameState.gameId,
      };
      sendMessage(req);
    };

    const showResults = () => {
      if (!gameState.gameId || gameState.state === GameState.SHOW_RESULTS) {
        return;
      }

      setLoading(true);
      const req: ShowResultsRequest = {
        action: Actions.showResults,
        gameId: gameState.gameId,
      };
      sendMessage(req);
    };

    // Host only. isConnected is checked because sendMessage drops the message when the
    // socket is down, leaving the button spinning for a reply that never comes.
    const requestAnswerRecapMatrix = () => {
      if (!gameState.gameId || !isConnected.value || isLoadingAnswerRecap.value) {
        return;
      }
      isLoadingAnswerRecap.value = true;
      const req: GetAnswerRecapMatrixRequest = {
        action: Actions.getAnswerRecapMatrix,
        gameId: gameState.gameId,
      };
      sendMessage(req);
    };

    const removePlayer = (playerId: string) => {
      if (!gameState.gameId || gameState.state !== GameState.LOBBY) {
        return;
      }
      isRemoving.value[playerId] = true;
      const req = {
        action: Actions.removePlayer,
        gameId: gameState.gameId,
        playerId,
      };
      sendMessage(req);
    };

    const setReturnParams = (rParams: ReturnParams) => {
      returnParams.value = rParams;
    };

    watchEffect(() => {
      if (allPlayersHaveAnswered.value) {
        showCorrectAnswer();
      }
    });

    watch(isGameFinished, (finished) => {
      if (finished) {
        sendGameEndEvent();
      }
    });

    return {
      // Spread game state as individual refs
      ...toRefs(gameState),

      // UI State
      isConnected,
      status,
      isLoading,
      isRemoving,
      isReconnecting,
      returnParams: computed(() => returnParams.value),
      errorType,
      errorMessage,

      // Computed
      questionCountdown,
      isShowingResults,
      timeLeft,
      isLobbyFull,
      isLoadingAnswerRecap,
      isGameFinished,

      // Actions
      connect,
      disconnect,
      sendMessage,
      setGameId,
      reset,
      setGameOptions,
      startGame,
      setLoading,
      createGame,
      fetchMyQuizters,
      saveQuizter,
      getQuizter,
      updateQuizter,
      deleteQuizter,
      shareQuizter,
      checkQuizterShare,
      unshareQuizter,
      fetchMySubscribedQuizters,
      getSharedQuizter,
      subscribeSharedQuizter,
      unsubscribeSharedQuizter,
      showQuestionPhrase,
      nextQuestion,
      showCorrectAnswer,
      showResults,
      requestAnswerRecapMatrix,
      joinGameHost,
      setReturnParams,
      setSelectedAnswer,
      removePlayer,
    };
  },
  {
    //@ts-ignore pinia-plugin-persistedstate, missing types ?
    persist: {
      storage: sessionStorage,
      pick: ['gameId', 'myAvatar', 'state', 'resourceId', 'playerId'],
      debug: true,
    },
  },
);
